import { ArrowRight } from "lucide-react";
import type * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { BlockTitle } from "./shared";

/**
 * 链路总览第一个页签：问题与主张（用户 10-25）。内容正本是《基线_01_原则与策略》「零、核心问题与主张」，
 * 这里只照抄展示，改口径先改基线再改这里。版式照页面地图：左边核心问题 → 中间三组（每组两行「问题 → 主张」）→ 右边规模化。
 */

type Pair = {
	problem: string;
	problemText: string;
	claim: string;
	claimText: string;
	todo?: boolean;
};
type Dim = { key: string; label: string; pairs: [Pair, Pair] };

export const CORE_PROBLEM = "客服 Agent 规模化不了";
export const CORE_CLAIM = "把客服行业经验沉淀进产品，边界清楚，一线靠使用指南和 AI 运营助手交付";

export const DIMS: Dim[] = [
	{
		key: "product",
		label: "产品化",
		pairs: [
			{
				problem: "办不成事",
				problemText: "客户的事推进不到结果，还会说错、说办了其实没办，商家不敢放手",
				claim: "推进服务有结果",
				claimText: "每件事都要有结果，办成、给出路或转人工，不说没办的事",
			},
			{
				problem: "太通用，没有行业沉淀",
				problemText: "像通用编排工具，客服经验没进产品，每个客户都从零搭，难度大、周期长",
				claim: "行业经验产品化",
				claimText: "先把客服经验沉淀进应答架构，其次才是行业业务包",
			},
		],
	},
	{
		key: "arch",
		label: "架构演进",
		pairs: [
			{
				problem: "边界没定",
				problemText: "模型、代码、客户配置各管什么，还没有验证过的答案",
				claim: "模型、代码、客户边界明确",
				claimText: "模型管判断，代码管事实和底线，客户管业务内容",
			},
			{
				problem: "效果不通用",
				problemText:
					"效果是针对已知问题一个个修出来的，没有沉淀成通用能力，换成没见过的对话就掉一半",
				claim: "一切改动以边界定义为准",
				claimText: "每层每个模块都有边界定义，问题定位到层，修机制不修题",
			},
		],
	},
	{
		key: "delivery",
		label: "交付效率",
		pairs: [
			{
				problem: "个性化不会改",
				problemText:
					"一线手里没有验证过的客服业务经验框架，标品效果达不到客户要求，按客户调又不知道该改哪、怎么改",
				claim: "使用指南",
				claimText: "基于边界定义，写清每类问题改哪一层、怎么改",
				todo: true,
			},
			{
				problem: "改了没法验证",
				problemText: "改一处容易把别的场景改坏，最后变成一堆无法运营优化的提示词",
				claim: "AI 运营助手",
				claimText: "参考 Fin，帮一线发现问题、定位到层、给出改法、批量验证",
				todo: true,
			},
		],
	},
];

const PROBLEM_LOOK = "border-brand-warning/40 bg-brand-warning/5";
const CLAIM_LOOK = "border-brand-success/40 bg-brand-success/5";

export function NorthStar() {
	return (
		<Card data-testid="plan-block-north">
			<CardContent className="space-y-4 p-6">
				<BlockTitle
					title="问题与主张"
					sub="技术、产品、客户交付后运行的演进都以这里为最高方向；任何需求、改动、实验，先答它解决哪个问题、符不符合对应的主张"
					right={<NorthLegend />}
				/>
				<div
					className="rounded-lg border border-surface-border bg-surface-background px-4 py-3 text-sm"
					data-testid="plan-north-claim"
				>
					<span className="font-semibold text-text-primary">核心主张：</span>
					<span className="text-text-primary">{CORE_CLAIM}</span>
				</div>
				<div className="grid items-stretch gap-3 xl:grid-cols-[8rem_auto_minmax(0,1fr)_auto_7rem]">
					<EndBox
						look={PROBLEM_LOOK}
						label="核心问题"
						text={CORE_PROBLEM}
						testId="plan-north-core"
					/>
					<Arrow />
					<div className="space-y-3">
						{DIMS.map((d) => (
							<DimBox key={d.key} d={d} />
						))}
					</div>
					<Arrow />
					<EndBox look={CLAIM_LOOK} label="目标" text="规模化" testId="plan-north-goal" />
				</div>
				<p className="text-xs text-text-secondary">
					使用指南定规矩，AI 运营助手帮一线按规矩做，两者相辅相成。正本：《基线 01 ·
					原则与策略》第零节。
				</p>
			</CardContent>
		</Card>
	);
}

function NorthLegend() {
	return (
		<div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
			<span className="inline-flex items-center gap-1">
				<span className={cn("h-3 w-3 rounded-sm border", PROBLEM_LOOK)} aria-hidden />
				问题
			</span>
			<span className="inline-flex items-center gap-1">
				<span className={cn("h-3 w-3 rounded-sm border", CLAIM_LOOK)} aria-hidden />
				主张
			</span>
			<Badge tone="muted">待建</Badge>
		</div>
	);
}

function EndBox({
	look,
	label,
	text,
	testId,
}: { look: string; label: string; text: string; testId: string }) {
	return (
		<div
			className={cn(
				"flex flex-col items-center justify-center gap-1 rounded-lg border px-3 py-4 text-center",
				look,
			)}
			data-testid={testId}
		>
			<span className="text-xs text-text-secondary">{label}</span>
			<span className="text-sm font-semibold text-text-primary">{text}</span>
		</div>
	);
}

function Arrow() {
	return (
		<span className="hidden items-center justify-center text-brand-primary xl:flex" aria-hidden>
			<ArrowRight className="h-5 w-5" strokeWidth={2.5} />
		</span>
	);
}

function DimBox({ d }: { d: Dim }) {
	return (
		<div
			className="space-y-2 rounded-lg border border-surface-border bg-surface-background p-3"
			data-testid={`plan-north-dim-${d.key}`}
		>
			<div className="border-l-4 border-l-brand-primary pl-2 text-sm font-semibold text-text-primary">
				{d.label}
			</div>
			{d.pairs.map((p) => (
				<div
					key={p.problem}
					className="grid items-stretch gap-2 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
				>
					<Cell look={PROBLEM_LOOK} title={p.problem} text={p.problemText} />
					<span className="hidden items-center text-text-muted lg:flex" aria-hidden>
						<ArrowRight className="h-4 w-4" />
					</span>
					<Cell look={CLAIM_LOOK} title={p.claim} text={p.claimText} todo={p.todo} />
				</div>
			))}
		</div>
	);
}

function Cell({
	look,
	title,
	text,
	todo,
}: { look: string; title: string; text: string; todo?: boolean }): React.ReactElement {
	return (
		<div className={cn("flex flex-col gap-1 rounded-md border px-3 py-2.5", look)}>
			<span className="flex items-center gap-1.5">
				<span className="text-sm font-semibold text-text-primary">{title}</span>
				{todo ? (
					<Badge tone="muted" className="shrink-0 whitespace-nowrap">
						待建
					</Badge>
				) : null}
			</span>
			<span className="text-xs leading-snug text-text-secondary">{text}</span>
		</div>
	);
}
